/**
 * Shared helpers for generation-related Zustand stores.
 * Both generationStore (keyed by imageId) and visionBoardStore (keyed by projectId)
 * use identical patterns for pending generations and data caching.
 */

/**
 * Create pending-generation state slice for a Zustand store.
 * @param {'keyed'|'flat'} style
 *   - 'keyed': pendingGenerations is { [key]: [...items] } (generationStore — keyed by imageId)
 *   - 'flat': pendingGenerations is [...items] (visionBoardStore — flat array)
 */
export function createPendingSlice(style) {
  if (style === 'keyed') {
    return {
      pendingGenerations: {},

      addPendingGeneration: (data) => (set, get) => {
        const key = data.imageId;
        const existing = get().pendingGenerations[key] || [];
        if (existing.some((p) => p.generationId === data.generationId)) return;
        set({
          pendingGenerations: {
            ...get().pendingGenerations,
            [key]: [{ ...data, startTime: Date.now() }, ...existing],
          },
        });
      },

      removePendingGeneration: (key, generationId) => (set, get) => {
        const existing = get().pendingGenerations[key];
        if (!existing) return;
        const filtered = existing.filter((p) => p.generationId !== generationId);
        if (filtered.length === 0) {
          const { [key]: _, ...rest } = get().pendingGenerations;
          set({ pendingGenerations: rest });
        } else {
          set({
            pendingGenerations: { ...get().pendingGenerations, [key]: filtered },
          });
        }
      },
    };
  }

  // flat style
  return {
    pendingGenerations: [],

    addPendingGeneration: (data) => (set, get) => {
      if (get().pendingGenerations.some((p) => p.generationId === data.generationId)) return;
      set({
        pendingGenerations: [{ ...data, startTime: Date.now() }, ...get().pendingGenerations],
      });
    },

    removePendingGeneration: (generationId) => (set, get) => {
      set({
        pendingGenerations: get().pendingGenerations.filter((p) => p.generationId !== generationId),
      });
    },
  };
}

/**
 * Create a cache-get helper: returns cached entry or null.
 */
export function getCached(cache, key) {
  return cache[key] || null;
}

/**
 * Create an updated cache object with a new entry (immutable).
 */
export function setCache(cache, key, data) {
  return { ...cache, [key]: { ...data, fetchedAt: Date.now() } };
}

/**
 * Create an updated cache object with a key removed (immutable).
 */
export function invalidateCache(cache, key) {
  if (!cache[key]) return cache;
  const { [key]: _, ...rest } = cache;
  return rest;
}
